import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Loader2, MapPin } from 'lucide-react';
import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { planRecipients, wantsEmail, wantsSms, type Channel } from '@/lib/recipientPlan';
import {
    ApiCallError, previewConsigneePin, sendConsigneePin, type ConsigneePinPreview,
} from '@/api/client';

/**
 * SendConsigneePinDialog — text the driver a pin to where he is DELIVERING (#324).
 *
 * Pete, 2026-08-27: *"Can we send a pin to the location and a .pdf?"*
 *
 * ---- ⛔ THIS IS NOT SEND DIRECTIONS ----------------------------------------
 *
 * ⚠️ Directions answer *"how do I get to YOU"* — our yard, before he sets off.
 * This answers *"where am I taking this"* — the drop, which is somebody else's
 * site entirely. ⭐ A driver may hold both messages, so the two are worded
 * differently on every surface and are separate menu items. Folding them
 * together would put one map link in front of a man who needs the other.
 *
 * ---- ⭐ THE PREVIEW IS THE POINT, NOT A COURTESY ---------------------------
 *
 * The dialog opens by asking the server what it WOULD send, and shows it. Two
 * things come out of that, and both matter more than they look:
 *
 *   · ⛔ WHEN THERE IS NO PIN, THE REASON NAMES THE FIX. "LOWES-7741 is not in
 *     the address book — add it in Settings → Consignees" is a job somebody can
 *     do. "No pin available" is a sentence that ends the conversation.
 *   · ⚠️ THE SEGMENT COUNT IS SHOWN BEFORE ANYTHING IS SENT. A consignee's
 *     address is typed by a person; one curly quote pasted from Word forces the
 *     whole message to UCS-2 and cuts a segment from 160 characters to 70.
 *     ⛔ NOTHING HERE REWRITES IT — rewriting a gate instruction to save a
 *     segment is how a driver ends up at the wrong door. A human decides.
 *
 * ---- ⛔ AND THE MESSAGE IS SERVER-AUTHORED ---------------------------------
 *
 * Only the appointment id and the recipients travel. Every word that lands on a
 * phone is looked up server-side — the same rule SendDirectionsDialog follows,
 * for the same reason: message text a caller can dictate is message text an
 * attacker can dictate.
 */

interface Props {
    open: boolean;
    onClose: () => void;
    appointmentId: string;
    /** Pre-fill from the appointment's drivers. Only phones — email is not snapshotted. */
    drivers?: { name: string; phone: string }[];
    getToken: () => Promise<string>;
}

export function SendConsigneePinDialog({
    open, onClose, appointmentId, drivers = [], getToken,
}: Props) {
    const hasDrivers = drivers.length > 0;
    const driverPhones = drivers.map(d => d.phone);

    const [channel, setChannel] = useState<Channel>(hasDrivers ? 'sms' : 'email');
    const [emailInput, setEmailInput] = useState('');
    const [phoneInput, setPhoneInput] = useState('');
    const [emails, setEmails] = useState<string[]>([]);
    const [phones, setPhones] = useState<string[]>(driverPhones);
    const [sending, setSending] = useState(false);
    const [msg, setMsg] = useState<{ kind: 'ok' | 'warn' | 'err'; text: string } | null>(null);

    const [preview, setPreview] = useState<ConsigneePinPreview | null>(null);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [previewError, setPreviewError] = useState<string | null>(null);

    const loadPreview = useCallback(async () => {
        setLoadingPreview(true);
        setPreviewError(null);
        try {
            setPreview(await previewConsigneePin(await getToken(), appointmentId));
        } catch (e) {
            setPreview(null);
            setPreviewError(e instanceof ApiCallError ? e.message : 'Could not work out what would be sent.');
        } finally {
            setLoadingPreview(false);
        }
    }, [appointmentId, getToken]);

    useEffect(() => { if (open) void loadPreview(); }, [open, loadPreview]);

    const close = () => {
        setChannel(hasDrivers ? 'sms' : 'email');
        setEmailInput(''); setPhoneInput('');
        setEmails([]); setPhones(driverPhones);
        setMsg(null); setSending(false);
        setPreview(null); setPreviewError(null);
        onClose();
    };

    const addEmail = () => {
        const v = emailInput.trim();
        if (v && !emails.includes(v)) { setEmails([...emails, v]); setEmailInput(''); }
    };
    const addPhone = () => {
        const v = phoneInput.trim();
        if (v && !phones.includes(v)) { setPhones([...phones, v]); setPhoneInput(''); }
    };

    const handleSend = async () => {
        setMsg(null);
        const wantEmail = wantsEmail(channel);
        const wantSms = wantsSms(channel);

        /** ⛔ THE SHARED RULES — see @/lib/recipientPlan for what each one cost. */
        const plan = planRecipients({ channel, emails, phones, emailInput, phoneInput });
        if (plan.commit) {
            setEmails(plan.emails); setPhones(plan.phones);
            if (wantEmail) setEmailInput('');
            if (wantSms) setPhoneInput('');
        }
        if (!plan.ok) { setMsg({ kind: 'err', text: plan.error ?? 'Add at least one recipient.' }); return; }

        setSending(true);
        try {
            const res = await sendConsigneePin(await getToken(), appointmentId, {
                channel, emails: plan.emails, phones: plan.phones,
            });
            if (res.anyFailed) {
                setMsg({ kind: 'warn', text: `Sent ${res.sent}. Some recipients failed — check the list.` });
            } else {
                setMsg({ kind: 'ok', text: res.note ?? `Sent to ${res.sent} recipient${res.sent === 1 ? '' : 's'}.` });
            }
        } catch (e) {
            setMsg({ kind: 'err', text: e instanceof ApiCallError ? e.message : 'The pin could not be sent.' });
        } finally {
            setSending(false);
        }
    };

    const canSend = preview?.canSend === true && !sending;

    return (
        <Dialog open={open} onOpenChange={o => { if (!o) close(); }}>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <MapPin className="h-4 w-4" /> Text the delivery pin
                    </DialogTitle>
                    <DialogDescription>
                        Sends the driver the address, gate instructions and a map link for where
                        this appointment is going.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3">
                    {loadingPreview && (
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Working out what would be sent…
                        </div>
                    )}

                    {previewError && (
                        <div className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">{previewError}</div>
                    )}

                    {/*
                      * ⛔ THE BLOCKED CASE IS THE IMPORTANT ONE. It names the consignee
                      * and the screen to go to, because every way this can be blocked is
                      * a five-minute fix that somebody has to be told about.
                      */}
                    {preview && !preview.canSend && (
                        <div className="flex items-start gap-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
                            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                            <span>{preview.reason}</span>
                        </div>
                    )}

                    {preview?.canSend && (
                        <div className="space-y-1.5 rounded-md border bg-muted/40 px-3 py-2">
                            <p className="text-xs font-medium">
                                {preview.consigneeName}{' '}
                                <code className="text-muted-foreground">{preview.consigneeCode}</code>
                            </p>
                            <p className="whitespace-pre-wrap break-words text-xs text-muted-foreground">
                                {preview.sms}
                            </p>
                            <p className="text-xs text-muted-foreground">
                                {preview.segments} segment{preview.segments === 1 ? '' : 's'}
                                {/*
                                  * ⚠️ SAYS *WHY* IT IS EXPENSIVE, not just that it is. "2
                                  * segments" prompts nothing; naming the cause points at
                                  * the character that can be removed — by a person, in
                                  * Settings, not by this code.
                                  */}
                                {preview.unicode && (
                                    <span className="text-amber-700">
                                        {' '}— an accented letter, curly quote or dash in the address or gate
                                        instructions has more than doubled the cost. Editing it in
                                        Settings → Consignees would bring it down.
                                    </span>
                                )}
                            </p>
                            {!preview.smsEnabled && channel !== 'email' && (
                                <p className="text-xs text-amber-700">Texting is not enabled yet — email still goes.</p>
                            )}
                        </div>
                    )}

                    <div className="flex gap-1.5">
                        {(['email', 'sms', 'both'] as Channel[]).map(c => (
                            <button
                                key={c} type="button" onClick={() => setChannel(c)}
                                className={`rounded-md border px-3 py-1 text-xs ${channel === c
                                    ? 'border-blue-600 bg-blue-600 text-white'
                                    : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'}`}
                            >
                                {c === 'email' ? 'Email' : c === 'sms' ? 'Text' : 'Email + Text'}
                            </button>
                        ))}
                    </div>

                    {wantsEmail(channel) && (
                        <div>
                            <div className="flex gap-2">
                                <input
                                    type="email" value={emailInput}
                                    onChange={e => setEmailInput(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addEmail(); } }}
                                    placeholder="driver@example.com"
                                    className="flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                                />
                                <button type="button" onClick={addEmail}
                                    className="rounded-md border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50">Add</button>
                            </div>
                            {emails.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-1.5">
                                    {emails.map(em => (
                                        <span key={em} className="inline-flex items-center gap-1 rounded bg-blue-50 px-2 py-0.5 text-xs text-blue-700">
                                            {em}
                                            <button type="button" onClick={() => setEmails(emails.filter(x => x !== em))}
                                                className="text-blue-400 hover:text-blue-700">×</button>
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {wantsSms(channel) && (
                        <div>
                            <div className="flex gap-2">
                                <input
                                    type="tel" value={phoneInput}
                                    onChange={e => setPhoneInput(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addPhone(); } }}
                                    placeholder="+1 555 555 5555"
                                    className="flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                                />
                                <button type="button" onClick={addPhone}
                                    className="rounded-md border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50">Add</button>
                            </div>
                            {phones.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-1.5">
                                    {phones.map(ph => (
                                        <span key={ph} className="inline-flex items-center gap-1 rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
                                            {ph}
                                            <button type="button" onClick={() => setPhones(phones.filter(x => x !== ph))}
                                                className="text-gray-400 hover:text-gray-700">×</button>
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {msg && (
                        <div className={`rounded-md px-3 py-2 text-xs ${msg.kind === 'ok'
                            ? 'bg-green-50 text-green-700'
                            : msg.kind === 'warn' ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'}`}>
                            {msg.text}
                        </div>
                    )}

                    <div className="flex justify-end gap-2 pt-1">
                        <Button variant="ghost" size="sm" onClick={close} disabled={sending}>Close</Button>
                        <Button variant="default" size="sm" onClick={() => void handleSend()} disabled={!canSend}>
                            {sending ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : null}
                            Send pin
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
